import React, { useState, useContext, useEffect } from 'react';
import { ApiContext } from '../../App';
import { Info, Briefcase, ChevronRight } from 'lucide-react';
import { toast } from 'react-toastify';
import { normalizeEmailsForBackend } from '../../utils/emailHelpers';

const AddIndividual = ({ onClose, refreshIndividuals, preSelectedPosition = null, preSelectedStage = null }) => {
  const API = useContext(ApiContext);
  const [formData, setFormData] = useState({
    Name: '',
    phoneNumber: '',
    formation: '',
    personalEmail: '',
    professionalEmail: '',
    company: '',
    jobTitle: '',
    city: '',
    resume: null,
    linkedinUrl: '',
  });
  const [uploadingFile, setUploadingFile] = useState(false);

  // États pour l'ajout à un projet
  const [addToProject, setAddToProject] = useState(!!preSelectedPosition);
  const [projectType, setProjectType] = useState('recruitment'); // 'recruitment' ou 'bizdev'
  const [positions, setPositions] = useState([]);
  const [processBizDevs, setProcessBizDevs] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState(preSelectedPosition || '');
  const [selectedBizDev, setSelectedBizDev] = useState('');
  const [selectedStage, setSelectedStage] = useState(preSelectedStage || '');
  const [projectComment, setProjectComment] = useState('');
  const [loadingProjects, setLoadingProjects] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Charger les positions depuis l'API
  const fetchPositions = async () => {
    setLoadingProjects(true);
    try {
      const response = await API.get('/api/positions');
      const openPositions = response.data.filter((pos) => pos.open === true);

      // Les positions incluent déjà les steps depuis l'API
      // Filtrer uniquement les steps actifs pour chaque position
      const positionsWithActiveSteps = openPositions.map(pos => ({
        ...pos,
        steps: pos.steps?.filter(step => step.active).sort((a, b) => a.order - b.order) || []
      }));

      setPositions(positionsWithActiveSteps);
    } catch (error) {
      console.error('Error fetching positions:', error);
      toast.error('Erreur lors du chargement des positions');
    } finally {
      setLoadingProjects(false);
    }
  };

  // Charger les processus BizDev depuis l'API
  const fetchProcessBizDevs = async () => {
    setLoadingProjects(true);
    try {
      const response = await API.get('/api/process-bizdev');
      const openBizDevProcess = response.data.filter((res) => res.active === true);
      setProcessBizDevs(openBizDevProcess);
    } catch (error) {
      console.error('Error fetching process bizdev:', error);
      toast.error('Erreur lors du chargement des processus BizDev');
    } finally {
      setLoadingProjects(false);
    }
  };

  // Charger les projets au montage si "Ajouter à un projet" est activé
  useEffect(() => {
    if (addToProject) {
      if (projectType === 'recruitment') {
        fetchPositions();
      } else {
        fetchProcessBizDevs();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addToProject, projectType]);

  // Charger automatiquement les positions si une position est présélectionnée
  useEffect(() => {
    if (preSelectedPosition) {
      fetchPositions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Vérifier que c'est un PDF
    if (file.type !== 'application/pdf') {
      toast.error('Seuls les fichiers PDF sont acceptés');
      return;
    }

    // Vérifier la taille (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Le fichier est trop volumineux (max 5MB)');
      return;
    }

    // Mettre à jour le fichier dans le formulaire
    setFormData(prev => ({ ...prev, resume: file }));

    // Extraire les informations du CV (SANS créer l'individual)
    setUploadingFile(true);
    try {
      const extractFormData = new FormData();
      extractFormData.append('cv', file);

      const response = await API.post('/api/individuals/extract-cv-data-only', extractFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success && response.data.extractedInfo) {
        const extracted = response.data.extractedInfo;

        // Remplir automatiquement les champs vides du formulaire
        setFormData(prev => ({
          ...prev,
          Name: prev.Name || extracted.name || '',
          professionalEmail: prev.professionalEmail || extracted.professionalEmail || '',
          personalEmail: prev.personalEmail || extracted.personalEmail || '',
          phoneNumber: prev.phoneNumber || extracted.phone || '',
          city: prev.city || extracted.city || '',
          jobTitle: prev.jobTitle || extracted.jobTitle || '',
          // Note: company et formation sont désactivés dans l'extraction mais on les laisse au cas où
          company: prev.company || extracted.company || '',
          formation: prev.formation || extracted.formation || '',
        }));

        toast.success('Informations extraites du CV avec succès !');
      }
    } catch (error) {
      console.error('Error extracting CV data:', error);
      toast.warning('CV sélectionné mais impossible d\'extraire les informations automatiquement. Vous pouvez remplir le formulaire manuellement.');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = new FormData();

    // Ajouter tous les champs sauf le resume et les emails
    for (const key in formData) {
      if (key !== 'resume' && key !== 'personalEmail' && key !== 'professionalEmail' && formData[key] !== null) {
        form.append(key, formData[key]);
      }
    }

    // Ajouter les emails directement (le backend les transformera en tableau)
    if (formData.professionalEmail) {
      form.append('professionalEmail', formData.professionalEmail.trim());
    }
    if (formData.personalEmail) {
      form.append('personalEmail', formData.personalEmail.trim());
    }

    // Ajouter le fichier resume séparément s'il existe
    if (formData.resume) {
      form.append('resume', formData.resume);
    }

    try {
      const response = await API.post('/api/individuals', form, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const createdIndividual = response.data;
      toast.success('Candidat créé avec succès');

      // Si "Ajouter à un projet" est activé, ajouter le candidat au projet sélectionné
      if (addToProject && selectedStage) {
        try {
          if (projectType === 'recruitment' && selectedPosition) {
            // Ajouter au processus de recrutement
            await API.post(`/api/positions/${selectedPosition}/candidates`, {
              individualId: createdIndividual._id,
              stageId: selectedStage,
              comment: projectComment.trim()
            });
            toast.success('Candidat ajouté au projet de recrutement');
          } else if (projectType === 'bizdev' && selectedBizDev) {
            // Ajouter au processus BizDev
            await API.post(`/api/process-bizdev/${selectedBizDev}/prospects`, {
              individualId: createdIndividual._id,
              step: selectedStage,
              notes: projectComment.trim()
            });
            toast.success('Candidat ajouté au projet de prospection');
          }
        } catch (projectError) {
          console.error('Error adding to project:', projectError);
          toast.error('Candidat créé mais erreur lors de l\'ajout au projet');
        }
      }

      refreshIndividuals();
      onClose();
    } catch (error) {
      console.error('Error adding individual:', error);
      toast.error('Erreur lors de la création du candidat');
    }
  };

  // Fonction pour gérer l'upload du fichier CSV/Excel
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['.csv', '.xlsx', '.xls'];
    const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));

    if (!allowedTypes.includes(fileExtension)) {
      toast.error('Format de fichier non supporté. Veuillez uploader un fichier .csv, .xlsx ou .xls');
      return;
    }

    setUploadingFile(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await API.post('/api/individuals/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload: ${percentCompleted}%`);
        }
      });

      if (response.data.success) {
        const { imported, errors, total } = response.data;

        toast.success(`Importation réussie ! ${imported} profil(s) importé(s) sur ${total}${errors > 0 ? ` (${errors} erreur(s))` : ''}`, { autoClose: 5000 });

        // Afficher les erreurs s'il y en a
        if (response.data.errorDetails && response.data.errorDetails.length > 0) {
          console.log('Erreurs d\'importation:', response.data.errorDetails);
          toast.warning(`Consultez la console pour voir les détails des ${errors} erreur(s)`);
        }

        // Recharger les données et fermer la modale
        refreshIndividuals();
        onClose();
      }
    } catch (error) {
      console.error('Erreur lors de l\'importation:', error);
      toast.error(`Erreur lors de l'import: ${error.response?.data?.message || 'Erreur inconnue'}`);
    } finally {
      setUploadingFile(false);
      event.target.value = ''; // Reset l'input
    }
  };

  const fieldConfig = [
    { name: 'Name', required: true, label: 'Nom complet' },
    { name: 'personalEmail', type: 'personalEmail', label: 'Email personnel' },
    { name: 'professionalEmail', type: 'professionalEmail', label: 'Email pro' },
    { name: 'phoneNumber', label: 'Numéro de téléphone' },
    { name: 'company', label: 'Entreprise' },
    { name: 'formation', label: 'Formation' },
    { name: 'jobTitle', label: 'Titre du poste' },
    { name: 'city', label: 'Ville' },
    { name: 'linkedinUrl', label: 'URL LinkedIn', type: 'url' }
  ];

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
      {/* Overlay sombre avec effet de flou */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal avec contrainte de hauteur */}
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 relative z-10 max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <i className="bi bi-person-fill text-white"></i>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white m-0">Nouveau contact</h2>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors"
            >
              <i className="bi bi-x-lg text-xl"></i>
            </button>
          </div>
        </div>

        {/* Contenu scrollable */}
        <div className="overflow-y-auto flex-1 px-6 py-4">
          {/* Instructions */}
          <div className="mb-4 pb-2 px-4 pt-4 bg-gray-50 border-1 border-gray-300  rounded-lg ">
            <div className='flex items-start gap-3'>
              <div className="mt-0.5">
                <Info className="w-4 h-4 flex-shrink-0 text-gray-600" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-600 mb-2 font-medium">
                  Autres méthodes d'ajout :
                </p>
                <ul className="space-y-1.5">
                  <li className="flex items-center gap-2 text-xs text-gray-600">
                    <div className="w-1 h-1 bg-gray-400 rounded-full flex-shrink-0"></div>
                    <span>Glisser / déposer un CV sur la page "Contacts"</span>
                  </li>
                  <li className="flex items-center gap-2 text-xs">
                    <div className="w-1 h-1 bg-gray-400 rounded-full flex-shrink-0"></div>
                    <a
                      href="https://chromewebstore.google.com/detail/jobrise-extension/nmdolcmgfpcaknpheglofplobaakbbhb?hl=fr"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-indigo-600 hover:underline transition-colors"
                    >
                      Installer l'extension Chrome
                    </a>
                  </li>
                  <li className="flex items-center gap-2 text-xs">
                    <div className="w-1 h-1 bg-gray-400 rounded-full flex-shrink-0"></div>
                    <button
                      type="button"
                      onClick={() => document.getElementById('csv-upload-modal').click()}
                      className="text-gray-600 hover:text-indigo-600 hover:underline transition-colors text-left"
                    >
                      Importer un fichier .csv ou .xlsx
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Input file caché pour l'import CSV/Excel */}
          <input
            id="csv-upload-modal"
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />

          {/* Indicateur de chargement */}
          {uploadingFile && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                <span className="text-sm text-gray-700 font-medium">Import en cours...</span>
              </div>
            </div>
          )}

          {/* Formulaire */}
          <form onSubmit={handleSubmit} className="space-y-4" id="add-individual-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {fieldConfig.map((field) => (
              <div key={field.name}>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                <input
                  type={field.type || 'text'}
                  name={field.name}
                  value={formData[field.name]}
                  onChange={handleInputChange}
                  required={field.required}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder={`Entrez ${field.label.toLowerCase()}`}
                />
              </div>
            ))}
          </div>

          {/* Section: Ajouter à un projet */}
          <div className=" mt-6 border-t pt-4 w-2/3">
            <button
              type="button"
              onClick={() => setAddToProject(!addToProject)}
              className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-indigo-600 transition-colors w-full mb-3"
            >
              <ChevronRight
                size={18}
                className={`transition-transform ${addToProject ? 'rotate-90' : ''}`}
              />
              <Briefcase size={16} />
              <span>Ajouter à un projet</span>
            </button>

            {addToProject && (
              <div className=" space-y-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
                {/* Sélection du type de projet */}
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-2">
                    Type de projet :
                  </label>
                  <div className="flex space-x-2 w-fit">
                    <button
                      type="button"
                      onClick={() => {
                        setProjectType('recruitment');
                        setSelectedPosition('');
                        setSelectedBizDev('');
                        setSelectedStage('');
                        setProjectComment('');
                      }}
                      className={` flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                        projectType === 'recruitment'
                          ? 'bg-indigo-600 text-white'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Recrutement
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setProjectType('bizdev');
                        setSelectedPosition('');
                        setSelectedBizDev('');
                        setSelectedStage('');
                        setProjectComment('');
                      }}
                      className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                        projectType === 'bizdev'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Prospection
                    </button>
                  </div>
                </div>

                {/* Sélection du projet de recrutement */}
                {projectType === 'recruitment' && (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-2">
                        Projet de recrutement :
                      </label>
                      {loadingProjects ? (
                        <div className="bg-white rounded-lg p-3 text-center">
                          <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto" />
                        </div>
                      ) : positions.length > 0 ? (
                        <select
                          value={selectedPosition}
                          onChange={(e) => {
                            setSelectedPosition(e.target.value);
                            setSelectedStage('');
                            setProjectComment('');
                          }}
                          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="">Sélectionner un projet...</option>
                          {positions.map((position) => (
                            <option key={position._id} value={position._id}>
                              {position.title}{position.partner?.name ? ` - ${position.partner.name}` : ''}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-3 text-center">
                          <p className="text-xs text-slate-500">Aucun projet de recrutement disponible</p>
                        </div>
                      )}
                    </div>

                    {/* Sélection de l'étape pour recrutement */}
                    {selectedPosition && (
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-2">
                          Étape :
                        </label>
                        <select
                          value={selectedStage}
                          onChange={(e) => {
                            setSelectedStage(e.target.value);
                            const position = positions.find(p => p._id === selectedPosition);
                            const step = position?.steps?.find(s => s._id === e.target.value);
                            // Afficher le champ commentaire si l'étape contient 'ko', 'ok' ou 'offre'
                            if (step && ['ko', 'ok', 'offre'].some(keyword => step.name.toLowerCase().includes(keyword))) {
                              // Commentaire optionnel sauf pour KO
                            } else {
                              setProjectComment('');
                            }
                          }}
                          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="">Sélectionner une étape...</option>
                          {positions
                            .find(p => p._id === selectedPosition)
                            ?.steps?.filter(step => step.active)
                            .sort((a, b) => a.order - b.order)
                            .map((step) => (
                              <option key={step._id} value={step._id}>
                                {step.name}
                              </option>
                            ))}
                        </select>
                      </div>
                    )}
                  </>
                )}

                {/* Sélection du projet de prospection */}
                {projectType === 'bizdev' && (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-2">
                        Projet de prospection :
                      </label>
                      {loadingProjects ? (
                        <div className="bg-white rounded-lg p-3 text-center">
                          <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
                        </div>
                      ) : processBizDevs.length > 0 ? (
                        <select
                          value={selectedBizDev}
                          onChange={(e) => {
                            setSelectedBizDev(e.target.value);
                            setSelectedStage('');
                            setProjectComment('');
                          }}
                          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Sélectionner un projet...</option>
                          {processBizDevs.map((process) => (
                            <option key={process._id} value={process._id}>
                              {process.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-3 text-center">
                          <p className="text-xs text-slate-500">Aucun projet de prospection disponible</p>
                        </div>
                      )}
                    </div>

                    {/* Sélection de l'étape pour prospection */}
                    {selectedBizDev && (
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-2">
                          Étape :
                        </label>
                        <select
                          value={selectedStage}
                          onChange={(e) => setSelectedStage(e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Sélectionner une étape...</option>
                          {processBizDevs
                            .find(p => p._id === selectedBizDev)
                            ?.steps?.filter(step => step.active)
                            .map((step) => (
                              <option key={step._id} value={step.name}>
                                {step.name}
                              </option>
                            ))}
                        </select>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Upload de CV */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-slate-700 mb-1">
              CV (PDF)
            </label>
            <input
              type="file"
              name="resume"
              onChange={handleFileChange}
              accept=".pdf"
              className="sr-only"
              id="resume"
              tabIndex={-1}
            />
            <label
              htmlFor="resume"
              className="mt-1 flex flex-col justify-center items-center px-6 py-8 border-2 border-slate-300 border-dashed rounded-md cursor-pointer hover:border-indigo-400 hover:bg-indigo-50 transition-colors duration-200"
              onClick={(e) => {
                e.preventDefault();
                const scrollPos = document.querySelector('.overflow-y-auto')?.scrollTop;
                document.getElementById('resume').click();
                setTimeout(() => {
                  if (scrollPos !== undefined) {
                    document.querySelector('.overflow-y-auto').scrollTop = scrollPos;
                  }
                }, 0);
              }}
            >
              <div className="space-y-2 text-center">
                {uploadingFile ? (
                  <>
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    </div>
                    <p className="text-sm text-indigo-600 font-medium">
                      Extraction des informations en cours...
                    </p>
                  </>
                ) : (
                  <>
                    <svg
                      className="mx-auto h-12 w-12 text-slate-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                      aria-hidden="true"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="text-sm text-slate-600">
                      <span className="font-medium text-indigo-600 hover:text-indigo-500">
                        Cliquez pour télécharger
                      </span>
                      {' '}ou glissez-déposez
                    </div>
                    <p className="text-xs text-slate-500">
                      PDF jusqu'à 5 Mo
                    </p>
                    {formData.resume && (
                      <p className="text-xs text-indigo-600 font-medium mt-2">
                        ✓ {formData.resume.name}
                      </p>
                    )}
                  </>
                )}
              </div>
            </label>
          </div>
          </form>
        </div>

        {/* Buttons - fixés en bas */}
        <div className="flex justify-end space-x-3 px-6 py-4 border-t bg-gray-50 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-slate-300 rounded-md text-slate-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Annuler
          </button>
          <button
            type="submit"
            form="add-individual-form"
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Ajouter le candidat
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddIndividual;